/**
 * 🏛️ Town Hall — Miss Kitty Taylor-Nightingale + Police Chief Weasley Blanks
 *
 * Two NPCs, one page:
 *   😻 Miss Kitty hands out 50 rotating weekly quests (PT Monday reset)
 *   🦜 Weasley issues licenses + roams the world (catch him for bonus rewards)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  combat:  { icon: "🗡️", color: "bg-red-900/40 text-red-200 border-red-800/40" },
  farm:    { icon: "🌱", color: "bg-emerald-900/40 text-emerald-200 border-emerald-800/40" },
  diner:   { icon: "🍳", color: "bg-amber-900/40 text-amber-200 border-amber-800/40" },
  lore:    { icon: "📚", color: "bg-purple-900/40 text-purple-200 border-purple-800/40" },
  bounty:  { icon: "🪪", color: "bg-yellow-900/40 text-yellow-200 border-yellow-800/40" },
};

function formatCountdown(secs) {
  if (secs <= 0) return "Resetting...";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function TownHallPage() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeNpc, setActiveNpc] = useState(null);
  const [filter, setFilter] = useState("all");
  // 📜 Onboarding quest — for journal-aware Weasley/Taylor responses
  const [questState, setQuestState] = useState(null);
  const [journalReply, setJournalReply] = useState(null); // {npc_id, npc}

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/town-hall/state`);
      setState(r.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Town Hall closed.");
    }
  };

  const rollWeasley = async () => {
    try { await axios.post(`${API}/town-hall/weasley/roll`); refresh(); } catch {}
  };

  useEffect(() => {
    if (!isAuthed) return;
    refresh();
    // 📜 Load onboarding quest state too
    axios.get(`${API}/onboarding/state`).then(r => setQuestState(r.data)).catch(() => {});
    // Poll for Weasley spawn rolls every 5 min (backend throttles to 1/hr per user)
    const id = setInterval(rollWeasley, 5 * 60_000);
    return () => clearInterval(id);
  }, [isAuthed]);

  const showJournalTo = async (npcId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/ask-npc`, { npc_id: npcId });
      setJournalReply({ npc_id: npcId, npc: r.data.npc });
      setQuestState(r.data.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "They're not interested.");
    } finally {
      setBusy(false);
    }
  };

  const onClaim = async (quest) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/town-hall/quest/claim`, { quest_id: quest.quest_id });
      const reward = r.data?.reward || {};
      const chunks = [];
      if (reward.chits) chunks.push(`+${reward.chits} 🪙`);
      if (reward.od) chunks.push(`+${reward.od} Ⓞ`);
      if (reward.eggs?.length) chunks.push(`+${reward.eggs.length} 🥚`);
      toast.success(`✅ Quest claimed · ${chunks.join(" · ") || "Done!"}`);
      if (r.data?.licenses_unlocked?.length) {
        r.data.licenses_unlocked.forEach((lid) => {
          const lic = state?.licenses_catalog?.[lid];
          if (lic) toast.success(`🪪 License Unlocked — ${lic.name}`);
        });
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setBusy(false);
    }
  };

  const onCatchWeasley = async () => {
    if (!state?.weasley_spawn?.spawn_id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/town-hall/weasley/catch`, {
        spawn_id: state.weasley_spawn.spawn_id,
      });
      const reward = r.data?.reward || {};
      toast.success(`🦜 Caught Weasley! +${reward.chits || 0} 🪙 · +${reward.od || 0} Ⓞ`);
      if (r.data?.licenses_unlocked?.length) {
        r.data.licenses_unlocked.forEach((lid) => {
          const lic = state?.licenses_catalog?.[lid];
          if (lic) toast.success(`🪪 License Unlocked — ${lic.name}`);
        });
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "He flew off!");
    } finally {
      setBusy(false);
    }
  };

  // 🔄 Sep 2026 (Roc #28) — Miss Kitty's board picks 50 quests from a 12-template
  // pool WITH replacement, so each title repeats ~4×. Dedupe by title at render
  // time: pick the single BEST instance per title (prefer in-progress → ready →
  // claimed) and stash the full stack on `_dupes`/`_dupeIds` for the Claim
  // button to walk through. Users still see every reward, just one card at a time.
  const dedupedQuests = useMemo(() => {
    if (!state?.quests) return [];
    const score = (q) => {
      if (q.claimed) return 0;
      if (q.completed) return 2;         // ready-to-claim beats everything
      return 1;                           // in-progress / active
    };
    const byTitle = new Map();
    for (const q of state.quests) {
      const prev = byTitle.get(q.title);
      if (!prev || score(q) > score(prev)) {
        byTitle.set(q.title, { ...q, _dupeIds: [q.quest_id] });
      } else {
        prev._dupeIds.push(q.quest_id);
      }
    }
    // Attach stack size for the UI
    return Array.from(byTitle.values()).map((q) => ({
      ...q,
      _stack: q._dupeIds.length,
    }));
  }, [state]);

  const filteredQuests = useMemo(() => {
    if (filter === "all") return dedupedQuests;
    if (filter === "ready") return dedupedQuests.filter((q) => q.completed && !q.claimed);
    if (filter === "active") return dedupedQuests.filter((q) => !q.completed);
    if (filter === "claimed") return dedupedQuests.filter((q) => q.claimed);
    return dedupedQuests.filter((q) => q.kind === filter);
  }, [dedupedQuests, filter]);

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">🏛️</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            TOWN HALL
          </h1>
          <p className="text-neutral-400">Sign in to visit Miss Kitty & Chief Weasley.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const kitty = state?.npcs?.find((n) => n.id === "taylor_nightingale");
  const weasley = state?.npcs?.find((n) => n.id === "weasley_blanks");
  const cassandra = state?.npcs?.find((n) => n.id === "cassandra_auditor");
  const stats = state?.quest_stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100" data-testid="town-hall-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Hero strip */}
        <div className="rounded-2xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] via-black/60 to-[#1a0010] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">🏛️</div>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.4em] text-purple-300/70 mb-1">Kingdom of Dragonious</div>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🏛️ TOWN HALL
            </h1>
            <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">
              Where the kingdom's bureaucracy lives. Miss Kitty has errands. Chief Weasley has paperwork.
            </p>
            {state && (
              <div className="flex flex-wrap gap-3 mt-4 text-xs">
                <div className="rounded-full bg-black/60 border border-amber-700/40 px-3 py-1" data-testid="th-week-pill">
                  📅 Week {state.week_key} · resets in <b className="text-amber-200">{formatCountdown(state.seconds_to_reset)}</b>
                </div>
                <div className="rounded-full bg-black/60 border border-purple-700/40 px-3 py-1" data-testid="th-progress-pill">
                  ✅ <b className="text-amber-200">{stats.claimed}</b>/{stats.total} claimed · {stats.completed - stats.claimed} ready
                </div>
                <div className="rounded-full bg-black/60 border border-yellow-700/40 px-3 py-1" data-testid="th-license-pill">
                  🪪 Licenses: <b className="text-amber-200">{state.licenses_owned?.length || 0}</b>/{Object.keys(state.licenses_catalog || {}).length}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 🐦 Guild Hall pointer */}
        <Link
          to="/guild"
          data-testid="th-guild-pointer"
          className="block rounded-2xl border-2 border-indigo-700/50 bg-gradient-to-br from-indigo-950/30 via-black/60 to-violet-950/30 p-5 mb-6 hover:border-indigo-500/70 hover:shadow-[0_0_30px_-12px_rgba(99,102,241,0.6)] transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🐦</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-indigo-300/80">Visit</div>
              <div className="text-xl md:text-2xl font-black text-indigo-100" style={{ fontFamily: "Anton, sans-serif" }}>
                BIRDS OF A FEATHER — GUILD HALL
              </div>
              <p className="text-sm text-neutral-400 mt-1">
                Found a guild · join one · contribute Chits to your guild's Wealth · climb the perch.
              </p>
            </div>
            <div className="text-indigo-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
          </div>
        </Link>

        {/* ⚔️ Coliseum pointer */}
        <Link
          to="/coliseum"
          data-testid="th-coliseum-pointer"
          className="block rounded-2xl border-2 border-red-700/50 bg-gradient-to-br from-red-950/40 via-black/60 to-rose-950/30 p-5 mb-6 hover:border-red-500/70 hover:shadow-[0_0_30px_-12px_rgba(239,68,68,0.6)] transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">⚔️</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-red-300/80">Enter</div>
              <div className="text-xl md:text-2xl font-black text-red-100" style={{ fontFamily: "Anton, sans-serif" }}>
                THE COLISEUM — WAR HALL
              </div>
              <p className="text-sm text-neutral-400 mt-1">
                Form a 3-hero party · queue for 3v3 PvP or co-op Party Dungeons · random loot + shared Chits.
              </p>
            </div>
            <div className="text-red-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
          </div>
        </Link>

        {/* 🛡️ Battlegrounds pointer — 10v10 MOBA-lite (Feb 2026) */}
        <Link
          to="/battlegrounds"
          data-testid="th-battlegrounds-pointer"
          className="block rounded-2xl border-2 border-amber-700/50 bg-gradient-to-br from-amber-950/40 via-black/60 to-orange-950/30 p-5 mb-6 hover:border-amber-500/70 hover:shadow-[0_0_30px_-12px_rgba(245,158,11,0.6)] transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🛡️</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Brand New</div>
              <div className="text-xl md:text-2xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
                THE BATTLEGROUNDS — 10v10
              </div>
              <p className="text-sm text-neutral-400 mt-1">
                Queue up · escort Ambergris cargo · slay Sentinels · bank chests · climb the Honor leaderboard.
              </p>
            </div>
            <div className="text-amber-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
          </div>
        </Link>

        {/* 📜 Journal-aware banner — only when user is on the onboarding quest with the journal */}
        {questState?.journal && ["asking_around", "dave_overwhelmed", "gathering"].includes(questState.stage) && (
          <div className="rounded-2xl border-2 border-amber-700/60 bg-gradient-to-br from-amber-900/15 to-black/40 p-5 mb-6" data-testid="th-journal-banner">
            <div className="flex items-start gap-4">
              <div className="text-5xl flex-shrink-0">📜</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-300/70">Carrying</div>
                <div className="text-lg font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
                  CARI'S FAMILY JOURNAL
                </div>
                <p className="text-sm text-amber-100/85 italic font-serif mt-1">
                  Try showing it to either Weasley or Taylor. (Spoiler: neither will help.)
                </p>
                {!journalReply ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => showJournalTo("weasley")}
                      disabled={busy}
                      data-testid="show-weasley-journal-btn"
                      className="px-4 py-1.5 rounded bg-yellow-700 hover:bg-yellow-600 disabled:opacity-50 text-white font-bold text-xs"
                    >
                      🦜 Show Weasley
                    </button>
                    <button
                      onClick={() => showJournalTo("taylor")}
                      disabled={busy}
                      data-testid="show-taylor-journal-btn"
                      className="px-4 py-1.5 rounded bg-pink-700 hover:bg-pink-600 disabled:opacity-50 text-white font-bold text-xs"
                    >
                      😻 Show Taylor
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-purple-700/50 bg-black/40 p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-3xl">{journalReply.npc.icon}</div>
                      <div className="text-sm font-black text-amber-200">{journalReply.npc.name}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-purple-300/70 mb-2">{journalReply.npc.title}</div>
                    <div className="space-y-1.5 text-sm text-amber-100/95 font-serif">
                      {journalReply.npc.lines.map((l, i) => <p key={i}>{l}</p>)}
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-amber-300/80 italic">→ They both point to Dave at the Diner.</span>
                      <button
                        onClick={() => setJournalReply(null)}
                        className="ml-auto text-[10px] text-neutral-400 hover:text-amber-300 underline"
                      >
                        Show someone else
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Weasley spawn banner */}
        {state?.weasley_spawn && !state.weasley_spawn.claimed && (
          <div
            className="rounded-2xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-900/40 via-amber-900/40 to-yellow-900/40 p-4 mb-6 flex items-center gap-4 animate-pulse"
            data-testid="weasley-spawn-banner"
          >
            <div className="text-5xl">🦜</div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-yellow-300/80">Spotted!</div>
              <div className="text-lg font-black text-amber-200">Chief Weasley is roaming in the {state.weasley_spawn.area.toUpperCase()}!</div>
              <div className="text-xs text-amber-100/80 italic">"*squints at you* ...I knew you'd show up. 👀 Quick draw, Maw."</div>
            </div>
            <button
              onClick={onCatchWeasley}
              disabled={busy}
              data-testid="catch-weasley-btn"
              className="px-5 py-2 rounded bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-bold text-sm"
            >
              🪪 Catch Him →
            </button>
          </div>
        )}

        {/* NPC selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {[kitty, weasley, cassandra].filter(Boolean).map((npc) => (
            <button
              key={npc.id}
              onClick={() => setActiveNpc(npc)}
              data-testid={`npc-card-${npc.id}`}
              className={`relative rounded-xl border bg-gradient-to-br from-[#10001a] to-black/60 hover:shadow-[0_0_20px_rgba(245,158,11,0.18)] transition p-4 text-left flex items-start gap-4 ${
                npc.coming_soon
                  ? "border-amber-700/40 hover:border-amber-500/70"
                  : "border-purple-700/40 hover:border-amber-500"
              }`}
            >
              <div className="text-5xl flex-shrink-0">{npc.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-purple-300/70">{npc.title}</div>
                  {npc.coming_soon && (
                    <span
                      data-testid={`npc-coming-soon-${npc.id}`}
                      className="px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[8px] font-black uppercase tracking-widest"
                    >
                      🔒 Soon
                    </span>
                  )}
                </div>
                <div className="text-base font-black text-amber-200 truncate">{npc.name}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5">{npc.species}</div>
                <p className="text-xs text-amber-100/80 italic mt-2 line-clamp-2">"{npc.greeting}"</p>
              </div>
              <div className="text-amber-400">→</div>
            </button>
          ))}
        </div>

        {/* Quest filters */}
        <section className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              😻 MISS KITTY'S BOARD
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-purple-700/60 to-transparent" />
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { id: "all", label: "All" },
              { id: "ready", label: "✅ Ready to Claim" },
              { id: "active", label: "⏳ In Progress" },
              { id: "claimed", label: "🪙 Claimed" },
              { id: "combat", label: "🗡️ Combat" },
              { id: "farm", label: "🌱 Farm" },
              { id: "diner", label: "🍳 Diner" },
              { id: "lore", label: "📚 Lore" },
              { id: "bounty", label: "🪪 Bounty" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`th-filter-${f.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                  filter === f.id
                    ? "bg-amber-500 text-black border-amber-500"
                    : "bg-black/40 text-amber-200 border-purple-700/40 hover:border-amber-500"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {!state ? (
            <div className="text-neutral-500 text-sm">Miss Kitty is sorting her mail...</div>
          ) : filteredQuests.length === 0 ? (
            <div className="text-neutral-500 text-sm italic">No quests in this view.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="th-quest-grid">
              {filteredQuests.map((q) => (
                <QuestCard key={q.quest_id} q={q} busy={busy} onClaim={() => onClaim(q)} />
              ))}
            </div>
          )}
        </section>

        {/* License catalog */}
        <section className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🪪 LICENSE BOARD
            </h2>
            <div className="h-px flex-1 bg-gradient-to-r from-purple-700/60 to-transparent" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="th-license-board">
            {Object.values(state?.licenses_catalog || {}).map((lic) => {
              const owned = state?.licenses_owned?.includes(lic.id);
              return (
                <div
                  key={lic.id}
                  data-testid={`license-${lic.id}`}
                  className={`rounded-xl border p-4 transition ${
                    owned
                      ? "border-amber-500/70 bg-amber-500/5 shadow-[0_0_18px_rgba(245,158,11,0.18)]"
                      : "border-purple-700/40 bg-black/40 opacity-90"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-2xl">{lic.icon}</div>
                    {owned ? (
                      <div className="px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-black">✔ OWNED</div>
                    ) : (
                      <div className="px-2 py-0.5 rounded-full bg-neutral-800 text-neutral-400 text-[10px] font-black">🔒 LOCKED</div>
                    )}
                  </div>
                  <div className="text-base font-black text-amber-200">{lic.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{lic.tier}</div>
                  <div className="text-xs text-amber-100/80 mt-2">{lic.unlocks}</div>
                  <div className="text-[11px] text-neutral-500 italic mt-2">Earn: {lic.earn_path}</div>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* NPC greeting modal */}
      {activeNpc && (
        <NpcGreetingModal npc={activeNpc} onClose={() => setActiveNpc(null)} />
      )}

      <Footer />
    </div>
  );
}


function QuestCard({ q, busy, onClaim }) {
  const meta = KIND_META[q.kind] || KIND_META.combat;
  const pct = Math.min(100, Math.round((q.progress / Math.max(1, q.target_n)) * 100));
  const isClaimable = q.completed && !q.claimed;
  const isClaimed = q.claimed;
  return (
    <div
      data-testid={`quest-card-${q.quest_id}`}
      className={`rounded-xl border p-3 transition ${
        isClaimed
          ? "border-neutral-800 bg-black/40 opacity-60"
          : isClaimable
            ? "border-amber-500 bg-amber-500/5 shadow-[0_0_18px_rgba(245,158,11,0.18)]"
            : "border-purple-700/40 bg-black/40"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${meta.color}`}>
          {meta.icon} {q.kind}
        </span>
        <div className="flex items-center gap-1.5">
          {q._stack > 1 && (
            <span
              className="px-1.5 py-0.5 rounded-full text-[9px] font-black border border-purple-500/50 bg-purple-900/40 text-purple-200"
              title={`${q._stack} identical quests queued · the next one unlocks when you claim this`}
              data-testid={`quest-stack-${q.quest_id}`}
            >
              ×{q._stack}
            </span>
          )}
          {isClaimed && (
            <span className="text-[10px] font-black text-amber-400">✔ CLAIMED</span>
          )}
        </div>
      </div>
      <div className="text-sm font-black text-amber-100">{q.title}</div>
      <div className="text-[11px] text-neutral-400 mt-1 leading-snug">{q.desc}</div>
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-1">
          <span>Progress</span>
          <span><b className="text-amber-300">{q.progress}</b>/{q.target_n}</span>
        </div>
        <div className="h-1.5 bg-black/60 rounded-full overflow-hidden">
          <div
            className={`h-full ${isClaimed ? "bg-neutral-700" : isClaimable ? "bg-amber-500" : "bg-purple-600"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="text-[11px] text-neutral-400">
          {q.reward.chits ? <span className="mr-2">+{q.reward.chits} 🪙</span> : null}
          {q.reward.od ? <span className="mr-2">+{q.reward.od} Ⓞ</span> : null}
          {q.reward.eggs ? <span>+{q.reward.eggs} 🥚</span> : null}
        </div>
        {isClaimable && (
          <button
            onClick={onClaim}
            disabled={busy}
            data-testid={`claim-quest-${q.quest_id}`}
            className="px-3 py-1 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-[11px]"
          >
            Claim
          </button>
        )}
      </div>
    </div>
  );
}


function NpcGreetingModal({ npc, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="npc-greeting-modal">
      <div className="relative w-full max-w-2xl rounded-2xl border border-purple-700/50 bg-gradient-to-br from-[#1a0010] via-black to-[#10001a] p-6 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="npc-modal-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-amber-500/30 flex items-center justify-center text-amber-300 text-lg"
        >×</button>

        {npc.portrait_url && (
          <div className="rounded-xl overflow-hidden border border-amber-500/30 mb-5 bg-black/60">
            <img
              src={npc.portrait_url}
              alt={npc.name}
              data-testid={`npc-portrait-${npc.id}`}
              className="w-full h-auto object-cover"
              style={{ maxHeight: "320px", objectPosition: "center top" }}
            />
          </div>
        )}

        <div className="flex items-start gap-4 mb-5">
          <div className="text-6xl flex-shrink-0">{npc.icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/70">{npc.title}</div>
              {npc.coming_soon && (
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[9px] font-black uppercase tracking-widest">
                  🔒 Coming Soon
                </span>
              )}
            </div>
            <div className="text-2xl font-black text-amber-200">{npc.name}</div>
            <div className="text-[11px] text-neutral-500">{npc.species}</div>
          </div>
        </div>
        <div className="rounded-xl border border-neutral-800 bg-black/40 p-5 mb-4">
          <p className="text-base text-amber-100/95 leading-relaxed italic">"{npc.greeting}"</p>
          {npc.tagline && (
            <div className="text-[10px] uppercase tracking-widest text-purple-300/60 mt-3">{npc.tagline}</div>
          )}
        </div>
        {npc.id === "taylor_nightingale" && (
          <div className="text-[11px] text-neutral-500 italic">
            😌🎶 Taylor's not impressed. She's already posted 50 quests for you
            this week, and yes — that warbling is her actual meow. Calico fur,
            nightingale pipes, zero patience. Monday at 00:00 PT — board wipes.
          </div>
        )}
        {npc.id === "weasley_blanks" && (
          <div className="text-[11px] text-neutral-500 italic">
            🦜👀 Weasley roams the Farm · Wilds · Library · Kitchen · Diner. He's
            "just lookin' out for the kingdom." Catch him before he catches you doin'
            somethin' suspicious — and snag a license while you're at it.
          </div>
        )}
        {npc.id === "cassandra_auditor" && (
          <div className="space-y-3" data-testid="cassandra-lore-card">
            <div className="rounded-xl border border-amber-700/40 bg-amber-900/10 p-4">
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-1">📒 The Auditor's Brief</div>
              <p className="text-sm text-amber-100/90 leading-relaxed">
                Cassandra is the third pillar of the kingdom's information
                trade. Where Cari keeps the lore and Wesley keeps the law,
                <b className="text-amber-200"> Cassandra keeps the count.</b>
                Every monster slain in her ledger fills a wave: lore for
                Cari's books, anatomy for the field manual, and rare loot
                only her records can unlock.
              </p>
            </div>
            <div className="rounded-xl border border-purple-700/40 bg-purple-900/10 p-4">
              <div className="text-[10px] uppercase tracking-widest text-purple-300/80 mb-1">🕊️ About Ledger</div>
              <p className="text-sm text-amber-100/90 leading-relaxed">
                An old-fashioned courier pigeon — stocky, alert, and
                federally-registered. He flies the high lines and the back
                alleys, retrieving what no one else can reach. The cane on
                Cassandra's hip has a built-in perch. The gauntlet on her
                left arm is his runway. The monocle on her eye tracks every
                wingbeat. <b className="text-amber-200">Don't let Taylor eat him.</b>
              </p>
            </div>
            <div className="rounded-xl border border-neutral-800 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">🔒 Office Status</div>
              <div className="text-sm text-amber-100/80 mb-2">
                Cassandra's audit office is still being set up. Her combat
                kit, Scan ability, retrieval services, and 3-wave Audit
                Tracker are all parked until her arc launches.
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-0.5 rounded-full bg-black/60 border border-amber-700/40 text-amber-300 text-[10px]">🔒 Scan — soon</span>
                <span className="px-2 py-0.5 rounded-full bg-black/60 border border-amber-700/40 text-amber-300 text-[10px]">🔒 Audit Waves — soon</span>
                <span className="px-2 py-0.5 rounded-full bg-black/60 border border-amber-700/40 text-amber-300 text-[10px]">🔒 Retrieval — soon</span>
                <span className="px-2 py-0.5 rounded-full bg-black/60 border border-amber-700/40 text-amber-300 text-[10px]">🔒 Combat Hero — soon</span>
              </div>
            </div>
          </div>
        )}
        <div className="text-right mt-5">
          <button
            onClick={onClose}
            data-testid="npc-modal-got-it"
            className="px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm"
          >Got it</button>
        </div>
      </div>
    </div>
  );
}
